/**
 * Social Connections Service
 * 
 * Gerencia conexões OAuth com Instagram e TikTok via API
 * + Agendamento de posts
 * 
 * Fluxo simplificado: popup + polling no Composio SDK
 */

import axios from 'axios';
//https://hellosocial-backend.bluemushroom-1364dc44.brazilsouth.azurecontainerapps.io
// URL base da API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://hellosocial-backend.bluemushroom-1364dc44.brazilsouth.azurecontainerapps.io';

// Cliente axios configurado
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Helper para obter user_id
const getUserId = () => {
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      if (user?.id) return user.id;
    } catch (e) {}
  }
  return localStorage.getItem('user_id') || null;
};

// Interceptor para adicionar token de autenticação e user_id
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Adiciona user_id como query param
    const userId = getUserId();
    if (userId) {
      config.params = config.params || {};
      config.params.user_id = userId;
    }
    
    return config;
  },
  error => Promise.reject(error)
);

// Informações das plataformas
export const PLATFORMS = {
  instagram: {
    id: 'instagram',
    name: 'Instagram',
    icon: 'https://logos.composio.dev/api/instagram',
    color: 'from-pink-500 via-purple-500 to-indigo-500',
    bgColor: 'bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500',
    lightBg: 'bg-gradient-to-br from-pink-50 to-purple-50',
    description: 'Publique fotos, vídeos e reels no Instagram',
    supportedTypes: ['image', 'video'],
    features: [
      'Posts de imagem',
      'Vídeos no feed',
      'Reels',
      'Legendas personalizadas'
    ]
  },
  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'https://logos.composio.dev/api/tiktok',
    color: 'from-gray-900 via-gray-800 to-red-500',
    bgColor: 'bg-gradient-to-br from-gray-900 via-gray-800 to-red-500',
    lightBg: 'bg-gradient-to-br from-gray-50 to-red-50',
    description: 'Publique vídeos e fotos no TikTok',
    supportedTypes: ['image', 'video'],
    features: [
      'Vídeos curtos',
      'Fotos em carrossel',
      'Configurações de privacidade',
      'Descrições e hashtags'
    ]
  }
};

class SocialConnectionsService {
  
  /**
   * Busca todas as conexões do usuário
   * Agora usa verificação direta no Composio SDK
   */
  async getConnections() {
    try {
      const response = await apiClient.get('/api/social/connections');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar conexões:', error);
      throw error;
    }
  }

  /**
   * Verifica conexão diretamente no Composio SDK (sem depender do banco)
   * Esta é a rota chave para o polling após OAuth
   * 
   * @param {string} platform - instagram ou tiktok
   * @returns {Promise} - dados da conexão se existir
   */
  async checkConnectionFromComposio(platform) {
    try {
      const response = await apiClient.get(`/api/social/check-composio/${platform}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao verificar conexão ${platform} no Composio:`, error);
      throw error;
    }
  }

  /**
   * Conecta via OAuth abrindo popup e fazendo polling no Composio SDK
   * 
   * FLUXO SIMPLIFICADO:
   * 1. Solicita URL de OAuth ao backend
   * 2. Abre popup com a URL
   * 3. Quando popup fecha, faz polling para verificar se conexão foi criada
   * 4. Resolve quando encontrar conexão ativa no Composio
   * 
   * @param {string} platform - instagram ou tiktok
   * @param {number} timeout - timeout em ms (default 180000 = 3 min)
   * @returns {Promise} - resolve com dados da conexão ou reject com erro
   */
  async connectWithOAuth(platform, timeout = 180000) {
    return new Promise(async (resolve, reject) => {
      let popup = null;
      let pollInterval = null;
      let timeoutId = null;
      let isResolved = false;

      const cleanup = () => {
        if (pollInterval) clearInterval(pollInterval);
        if (timeoutId) clearTimeout(timeoutId);
      };

      const resolveOnce = (result) => {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          resolve(result);
        }
      };

      const rejectOnce = (error) => {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          reject(error);
        }
      };

      try {
        // 1. Solicita URL de OAuth ao backend
        console.log(`🔗 Solicitando URL OAuth para ${platform}...`);
        
        const response = await apiClient.post('/api/social/connect', {
          platform: platform
        });

        // Se já está conectado
        if (response.data?.already_connected) {
          console.log(`✅ Já conectado ao ${platform}`);
          resolveOnce({
            platform: platform,
            connected: true,
            already_connected: true,
            message: response.data.message
          });
          return;
        }

        const authUrl = response.data?.redirect_url;
        
        if (!authUrl) {
          rejectOnce(new Error('URL de autenticação não retornada pelo servidor'));
          return;
        }

        console.log(`🔗 URL OAuth recebida, abrindo popup...`);

        // 2. Abre o popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        popup = window.open(
          authUrl,
          `oauth_${platform}`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        if (!popup || popup.closed) {
          rejectOnce(new Error('Popup bloqueado. Por favor, permita popups para este site.'));
          return;
        }

        console.log(`📱 Popup aberto, iniciando polling...`);

        // 3. Polling para verificar se conexão foi estabelecida
        let pollCount = 0;
        const maxPolls = 60; // 60 tentativas x 3 segundos = 3 minutos
        let popupClosedCount = 0; // Conta quantas vezes verificou após popup fechar
        
        pollInterval = setInterval(async () => {
          pollCount++;
          
          // Verifica se popup foi fechado
          const popupClosed = popup.closed;
          
          if (popupClosed) {
            popupClosedCount++;
            console.log(`🔍 Popup fechado, verificando conexão (tentativa ${popupClosedCount})...`);
          }
          
          // Verifica conexão no Composio a cada poll (não só quando popup fecha)
          try {
            const connectionResult = await this.checkConnectionFromComposio(platform);
            
            if (connectionResult?.is_connected) {
              console.log(`✅ Conexão encontrada no Composio!`, connectionResult);
              resolveOnce({
                platform: platform,
                connected: true,
                ...connectionResult
              });
              return;
            }
          } catch (err) {
            // Ignora erros de polling, continua tentando
            if (popupClosed) {
              console.log(`⏳ Conexão ainda não disponível: ${err.message}`);
            }
          }
          
          // Se popup fechou e já tentou várias vezes, desiste
          if (popupClosed && popupClosedCount > 10) {
            rejectOnce(new Error('Conexão não encontrada. Se você completou a autenticação, aguarde alguns segundos e clique em "Atualizar".'));
            return;
          }
          
          // Timeout de polls
          if (pollCount >= maxPolls) {
            cleanup();
            if (popup && !popup.closed) {
              popup.close();
            }
            rejectOnce(new Error('Tempo esgotado. Por favor, tente novamente.'));
          }
        }, 3000); // Poll a cada 3 segundos

        // 4. Timeout geral
        timeoutId = setTimeout(() => {
          if (popup && !popup.closed) {
            popup.close();
          }
          rejectOnce(new Error('Tempo esgotado. Por favor, tente novamente.'));
        }, timeout);

      } catch (error) {
        cleanup();
        rejectOnce(error);
      }
    });
  }

  /**
   * Busca conexão de uma plataforma específica
   */
  async getConnection(platform) {
    try {
      const response = await apiClient.get(`/api/social/connections/${platform}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Inicia o fluxo OAuth (retorna apenas a URL, não abre popup)
   */
  async getOAuthUrl(platform) {
    try {
      const response = await apiClient.post('/api/social/connect', {
        platform: platform
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter URL OAuth ${platform}:`, error);
      throw error;
    }
  }

  /**
   * Desconecta uma plataforma
   */
  async disconnect(platform) {
    try {
      const response = await apiClient.delete(`/api/social/connections/${platform}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao desconectar ${platform}:`, error);
      throw error;
    }
  }

  /**
   * Publica conteúdo em uma ou mais plataformas
   */
  async publish(platforms, contentIds, batchId, options = {}) {
    try {
      const response = await apiClient.post('/api/social/publish/multiple', {
        platforms,
        content_ids: contentIds,
        batch_id: batchId,
        caption: options.caption,
        is_reel: options.isReel,
        privacy_level: options.privacyLevel
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao publicar:', error);
      throw error;
    }
  }

  /**
   * Publica um único conteúdo em uma plataforma
   */
  async publishSingle(platform, contentId, batchId, options = {}) {
    try {
      const response = await apiClient.post('/api/social/publish', {
        content_id: contentId,
        batch_id: batchId,
        platform: platform,
        caption: options.caption,
        is_reel: options.isReel,
        is_carousel: options.isCarousel || false,
        privacy_level: options.privacyLevel
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao publicar no ${platform}:`, error);
      throw error;
    }
  }

  // ===========================================================================
  // ✨ NOVOS MÉTODOS - AGENDAMENTO DE POSTS
  // ===========================================================================

  /**
   * Agenda um post para publicação futura
   * 
   * @param {string} platform - instagram ou tiktok
   * @param {string} contentId - ID do conteúdo
   * @param {string} batchId - ID do batch
   * @param {string} scheduledAt - Data/hora ISO 8601 (ex: "2026-01-25T14:00:00Z")
   * @param {string} caption - Legenda do post (opcional)
   * @param {string} timezone - Timezone do usuário (default: America/Sao_Paulo)
   * @returns {Promise} - dados do agendamento criado
   */
  async schedulePost(platform, contentId, batchId, scheduledAt, caption = '', timezone = 'America/Sao_Paulo', isCarousel = false) {
    try {
      const response = await apiClient.post('/api/social/schedule', {
        platform,
        content_id: contentId,
        batch_id: batchId,
        scheduled_at: scheduledAt,
        caption,
        timezone,
        is_carousel: isCarousel
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao agendar post:', error);
      throw error;
    }
  }

  /**
   * Lista posts agendados do usuário
   * 
   * @param {Object} options - Opções de filtro
   * @param {string} options.status - Filtrar por status (pending, published, failed, cancelled)
   * @param {string} options.platform - Filtrar por plataforma
   * @param {boolean} options.includePast - Incluir posts já publicados
   * @param {number} options.limit - Limite de resultados
   * @returns {Promise} - lista de posts agendados
   */
  async getScheduledPosts(options = {}) {
    try {
      const params = {};
      if (options.status) params.status = options.status;
      if (options.platform) params.platform = options.platform;
      if (options.includePast) params.include_past = options.includePast;
      if (options.limit) params.limit = options.limit;
      
      const response = await apiClient.get('/api/social/scheduled', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar posts agendados:', error);
      throw error;
    }
  }

  /**
   * Busca um post agendado específico
   * 
   * @param {string} postId - ID do post agendado
   * @returns {Promise} - dados do post agendado
   */
  async getScheduledPost(postId) {
    try {
      const response = await apiClient.get(`/api/social/scheduled/${postId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar post agendado:', error);
      throw error;
    }
  }

  /**
   * Atualiza um post agendado (data e/ou legenda)
   * 
   * @param {string} postId - ID do post agendado
   * @param {Object} updates - Campos a atualizar
   * @param {string} updates.scheduledAt - Nova data/hora (opcional)
   * @param {string} updates.caption - Nova legenda (opcional)
   * @returns {Promise} - post atualizado
   */
  async updateScheduledPost(postId, updates = {}) {
    try {
      const body = {};
      if (updates.scheduledAt) body.scheduled_at = updates.scheduledAt;
      if (updates.caption !== undefined) body.caption = updates.caption;
      
      const response = await apiClient.put(`/api/social/scheduled/${postId}`, body);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar agendamento:', error);
      throw error;
    }
  }

  /**
   * Cancela um post agendado
   * 
   * @param {string} postId - ID do post agendado
   * @param {boolean} permanent - Se true, deleta permanentemente
   * @returns {Promise} - confirmação
   */
  async cancelScheduledPost(postId, permanent = false) {
    try {
      const response = await apiClient.delete(`/api/social/scheduled/${postId}`, {
        params: { permanent }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao cancelar agendamento:', error);
      throw error;
    }
  }

  /**
   * Busca estatísticas do calendário do usuário
   * 
   * @returns {Promise} - estatísticas (total, por status, por plataforma)
   */
  async getCalendarStats() {
    try {
      const response = await apiClient.get('/api/social/calendar/stats');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  }

  // ===========================================================================
  // FIM - NOVOS MÉTODOS
  // ===========================================================================

  /**
   * Busca histórico de publicações
   */
  async getPublicationHistory(platform = null, limit = 50) {
    try {
      const params = { limit };
      if (platform) params.platform = platform;
      
      const response = await apiClient.get('/api/social/history', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      throw error;
    }
  }

  /**
   * Obtém informações da plataforma (helper local)
   */
  getPlatformInfo(platform) {
    return PLATFORMS[platform] || null;
  }

  /**
   * Lista todas as plataformas disponíveis
   */
  getAllPlatforms() {
    return Object.values(PLATFORMS);
  }

  /**
   * Verifica se um tipo de conteúdo é suportado
   */
  isContentTypeSupported(platform, contentType) {
    const info = PLATFORMS[platform];
    return info ? info.supportedTypes.includes(contentType) : false;
  }
}

export const socialConnectionsService = new SocialConnectionsService();
export default socialConnectionsService;